import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useCallback, useEffect, useState } from "react";
import { BillOfLadingReviewDialog } from "routes/Customer/Waste/Manifests/BillOfLadingReviewDialog";

import { type ManifestReviewRow } from "./api";
import { isBillOfLadingRow } from "./billOfLadingRow";
import { TenantFeatureFlagsProvider } from "./TenantFeatureFlagsProvider";

/**
 * Opens the bill review for a bill's source document under the row's own tenant. A discard closes and refreshes like a save.
 * The queue row only lists documents still needing review, so a reviewed bill resolves its source document from the manifest.
 */
export function BillOfLadingReviewHost({
  row,
  onClose,
  onSaved,
}: {
  row: Pick<ManifestReviewRow, "tenantId" | "facilityId" | "processedManifestId" | "documentIds">;
  onClose: () => void;
  onSaved: () => void;
}) {
  const listedDocumentId = row.documentIds[0];
  const { processedManifest, loading } = useProcessedManifest(
    listedDocumentId ? undefined : row.processedManifestId,
    row.tenantId
  );
  const documentId = listedDocumentId ?? processedManifest?.sourceDocument?.id;

  useEffect(() => {
    if (!documentId && !loading) onClose();
  }, [documentId, loading, onClose]);

  if (!documentId) return null;

  return (
    <TenantFeatureFlagsProvider tenantId={row.tenantId}>
      <BillOfLadingReviewDialog
        documentIds={[documentId]}
        facilityId={row.facilityId ?? ""}
        tenantId={row.tenantId}
        onClose={onClose}
        onSaved={onSaved}
        onDiscarded={onSaved}
      />
    </TenantFeatureFlagsProvider>
  );
}

/**
 * Bill review opened from a grid row. `open` returns false for a row that is not a bill,
 * so the caller can fall back to its manifest action.
 */
export function useBillOfLadingReview(onSaved: () => void) {
  const [target, setTarget] = useState<ManifestReviewRow | null>(null);
  const open = useCallback((row: ManifestReviewRow) => {
    if (!isBillOfLadingRow(row)) return false;
    setTarget(row);
    return true;
  }, []);
  const dialog = target ? (
    <BillOfLadingReviewHost
      row={target}
      onClose={() => setTarget(null)}
      onSaved={() => {
        setTarget(null);
        onSaved();
      }}
    />
  ) : null;
  return { open, dialog };
}
